import { BadgeCardRow } from '@bp/ui-components';
import { ClassGridCards } from '../../../../pages/Timetable/Plan/TimetableVersion/TimetableVersion';
import { Warnings } from '../../../../stores/TimetableVersion/PinboardStoreTypes';
import { SubjectContainerSlot } from '../../../../stores/TimetableVersion/SubjectContainerTypes';
import { ColorType } from '../../../../types/global';
import {
  _ClassesQuery,
  _GroupsQuery,
  _PeopleQuery,
  _SubjectContainersQuery,
  _SubjectsQuery,
  LessonEditStatus,
  LessonEditStatusReason,
} from '../../../../types/planung-graphql-client-defs';

/**
 * Die Form, in der die Stores der Stecktafel eine Karte führen. Gelesen wird sie nicht mehr so (die
 * Stecktafel liest timetableVersionData und baut diese Form daraus). Bis die Stores vom Lesemodell
 * abgeleitet sind, steht die Form hier ausgeschrieben – früher kam sie aus einem Fragment auf dem
 * Knotentyp TimetableCard, den es im Schema ohne @neo4j/graphql nicht mehr gibt.
 */
export type TimetableCardsType = {
  __typename: 'TimetableCard';
  uuid: string;
  duration?: number | null;
  weekday?: string | null;
  locked: boolean;
  startTimeGridEntry?: { __typename: 'TimeGridEntry'; uuid: string } | null;
  endTimeGridEntry?: { __typename: 'TimeGridEntry'; uuid: string } | null;
  rooms: Array<{ __typename: 'Room'; uuid: string }>;
  lessonClasses: Array<{
    __typename: 'LessonClass';
    uuid: string;
    usedDivision?: { __typename: 'Division'; uuid: string } | null;
    groups: Array<{ __typename: 'Group'; uuid: string }>;
    class: { __typename: 'Class'; uuid: string };
  }>;
  teachers: Array<{ __typename: 'Person'; uuid: string }>;
  lesson: { __typename: 'Lesson'; uuid: string };
  subject: { __typename: 'Subject'; uuid: string } | { __typename: 'SubjectContainer'; uuid: string };
};

/** Die Zuordnung einer Karte in den Stores der Stecktafel; früher aus einem Fragment auf dem Knotentyp Lesson. */
export type PinboardLessonType = {
  __typename: 'Lesson';
  uuid: string;
  subTitle?: string | null;
  studentCount?: number | null;
  comment?: string | null;
  onlyInTimetableVersion: boolean;
  elective?: boolean | null;
  isClassTrip?: boolean | null;
  teachingLoadEnabled?: boolean | null;
  timetableEnabled?: boolean | null;
  placedCardsCount?: number | null;
  teachingBlockPlacedCardsCount?: number | null;
  classSort: string;
  groupClassNames: string;
  gradeGroupUuid?: Array<string> | null;
  teachersSort: string;
  subjectName: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  editStatus?: {
    __typename?: 'LessonEditStatusInfo';
    status: LessonEditStatus;
    reasons: Array<{
      __typename?: 'LessonEditStatusReasonDetail';
      reason: LessonEditStatusReason;
      label: string;
      references: Array<{
        __typename?: 'LessonEditStatusReference';
        planUuid: string;
        planName: string;
        versionUuid?: string | null;
        version?: number | null;
        label: string;
      }>;
    }>;
  } | null;
  editStatusInVersion?: {
    __typename?: 'LessonEditStatusInfo';
    status: LessonEditStatus;
    reasons: Array<{
      __typename?: 'LessonEditStatusReasonDetail';
      reason: LessonEditStatusReason;
      label: string;
      references: Array<{
        __typename?: 'LessonEditStatusReference';
        planUuid: string;
        planName: string;
        versionUuid?: string | null;
        version?: number | null;
        label: string;
      }>;
    }>;
  } | null;
  curriculum?: { __typename: 'Curriculum'; uuid: string } | null;
  lessonUnitConnection: {
    __typename?: 'LessonLessonUnitConnection';
    edges: Array<{
      __typename?: 'LessonLessonUnitRelationship';
      properties: { __typename?: 'OrderedRelationProperties'; order: number };
      node: {
        __typename: 'LessonUnit';
        uuid: string;
        duration?: number | null;
        count?: number | null;
        subjectContainer?: { __typename: 'SubjectContainer'; uuid: string } | null;
      };
    }>;
  };
  lessonClassesConnection: {
    __typename?: 'LessonLessonClassesConnection';
    edges: Array<{
      __typename?: 'LessonLessonClassesRelationship';
      properties: { __typename?: 'OrderedRelationProperties'; order: number };
      node: {
        __typename: 'LessonClass';
        uuid: string;
        usedDivision?: { __typename: 'Division'; uuid: string } | null;
        groups: Array<{ __typename: 'Group'; uuid: string }>;
        class: { __typename: 'Class'; uuid: string };
      };
    }>;
  };
  teachersConnection: {
    __typename?: 'LessonTeachersConnection';
    edges: Array<{
      __typename?: 'LessonTeachersRelationship';
      properties: {
        __typename?: 'LessonTeacherRelationshipProperties';
        description?: string | null;
        order: number;
        present?: boolean | null;
        teachingLoad?: boolean | null;
        teachingLoadHours?: number | null;
        writesCertificate?: boolean | null;
      };
      node: { __typename: 'Person'; uuid: string };
    }>;
  };
  subject: { __typename: 'Subject'; uuid: string } | { __typename: 'SubjectContainer'; uuid: string };
  roomSupplyConnection: {
    __typename?: 'LessonRoomSupplyConnection';
    edges: Array<{
      __typename?: 'LessonRoomSupplyRelationship';
      properties: { __typename?: 'LessonRoomRelationshipProperties'; order: number; descriptions?: string | null };
      node: { __typename: 'Room'; uuid: string };
    }>;
  };
  versions: Array<{
    __typename: 'TimetableVersion';
    uuid: string;
    version: number;
    active?: boolean | null;
    timetable: { __typename?: 'Timetable'; uuid: string; name: string };
  }>;
  teachingBlockVersions: Array<{
    __typename: 'TeachingBlockVersion';
    uuid: string;
    version: number;
    active?: boolean | null;
    teachingBlock: { __typename?: 'TeachingBlock'; uuid: string; name: string };
  }>;
  activeTimetables?: Array<{ __typename: 'Timetable'; uuid: string }> | null;
  activeTeachingBlocks?: Array<{ __typename: 'Timetable'; uuid: string }> | null;
};
export type TimetableVersionTableType = {
  uuid: string;
  name: string;
  shortName: string;
  count: number;
  color: ColorType;
  grade?: number | null;
};

export type CardType = Omit<
  TimetableCardsType,
  | 'lesson'
  | 'subject'
  | 'teachers'
  | 'classes'
  | 'timetableVersion'
  | 'rooms'
  | 'startTimeGridEntry'
  | 'endTimeGridEntry'
> & {
  start: string | null;
  end: string | null;
  startTime: string | null; // format HH:mm (e.g. 08:00) utc
  endTime: string | null; // format HH:mm (e.g. 08:00) utc
  warnings?: Warnings;
  availabilityWarnings?: Warnings;
  subject?:
    | Pick<_SubjectsQuery, 'subjects'>['subjects'][0]
    | Pick<_SubjectContainersQuery, 'subjectContainers'>['subjectContainers'][0];
  isSubjectContainer: boolean;
  subjectContainerSlots?: SubjectContainerSlot[];
  lesson?: PinboardLessonType;
  classGridCards: ClassGridCards[];
  teachers: Pick<_PeopleQuery, 'people'>['people'];
  classes: Pick<_ClassesQuery, 'classes'>['classes'];
  groups: Pick<_GroupsQuery, 'groups'>['groups'];
  rooms: { uuid: string; name: string }[];
  badgeCardRows: BadgeCardRow[];
  badgeCardTextColor: string;
  // Karten mit einem dieser Flags dürfen sich im Board eine Cell teilen (nur mit anderen
  // solchen Karten – Mischen mit „normalen" Karten ist nicht erlaubt). Siehe isCoexistableCard.
  lessonIsElective: boolean;
  someLessonClassIsFunctionalClass: boolean;
  startTimeGridEntry?: { uuid: string } | null;
  endTimeGridEntry?: { uuid: string } | null;
};

/**
 * Karten, die sich im Board eine Cell mit anderen (ebenfalls koexistierbaren) Karten teilen
 * dürfen: Wahlunterricht (elective) oder Karten, deren LessonClass eine Funktionsklasse ist.
 * Mischen einer koexistierbaren mit einer nicht-koexistierbaren Karte ist nicht erlaubt.
 */
export const isCoexistableCard = (
  card: Pick<CardType, 'lessonIsElective' | 'someLessonClassIsFunctionalClass'>,
): boolean => card.lessonIsElective || card.someLessonClassIsFunctionalClass;
